import { useEffect } from "react";
import { Link, Redirect, useParams } from "wouter";
import { useAuth } from "../hooks/useAuth";
import {
  useCustomerInboxMessage,
  useMarkInboxMessageRead,
  useMarkInboxMessageUnread,
  type CustomerInboxMessageResponse,
} from "@orbit/api-client-react";
import { Wordmark } from "../components/Brand";
import { BottomNav } from "../components/BottomNav";
import { InboxMarkdown } from "../components/InboxMarkdown";

/**
 * /inbox/:id — single message detail.
 *
 * On first successful load we POST /read so the inbox badge clears.
 * The CTA shape depends on messageType (see api-zod discriminated
 * union): the hand-written switch below is what stops a future bug
 * from rendering an unsanitised CTA.
 */
type Msg = CustomerInboxMessageResponse["message"];

export function InboxMessagePage() {
  const { isAuthenticated } = useAuth();
  const params = useParams<{ id: string }>();
  const id = params.id;
  const msgQ = useCustomerInboxMessage(isAuthenticated ? id : undefined);
  const markRead = useMarkInboxMessageRead();
  const markUnread = useMarkInboxMessageUnread();

  // Auto-mark as read on first load.
  useEffect(() => {
    if (!msgQ.data) return;
    if (msgQ.data.message.readAt != null) return;
    markRead.mutate({ id });
    // markRead is a stable mutation hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgQ.data?.message.id]);

  if (!isAuthenticated) return <Redirect to="/login" />;

  return (
    <div className="min-h-screen pb-28">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link
            href="/inbox"
            className="text-sm text-ink-dark hover:text-navy"
          >
            ← Inbox
          </Link>
        </header>

        {msgQ.isLoading ? (
          <div className="mt-8 space-y-2" aria-label="Loading message">
            <div className="h-5 w-1/3 animate-pulse rounded bg-navy/10" />
            <div className="h-7 w-2/3 animate-pulse rounded bg-navy/10" />
            <div className="mt-4 h-4 w-full animate-pulse rounded bg-navy/5" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-navy/5" />
          </div>
        ) : msgQ.isError || !msgQ.data ? (
          <div className="mt-8 card text-center">
            <p className="text-sm text-red-700">
              We couldn't load this message. It may have been removed.
            </p>
            <Link
              href="/inbox"
              className="mt-4 inline-block text-sm font-semibold text-navy hover:text-navy/80"
            >
              Back to inbox
            </Link>
          </div>
        ) : (
          <article className="mt-6" data-testid="inbox-message">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-navy/60">
              {formatAuthor(msgQ.data.message)} •{" "}
              {labelForType(msgQ.data.message.messageType)}
            </p>
            <h1 className="mt-1 text-2xl font-bold text-navy">
              {msgQ.data.message.subject}
            </h1>
            <p className="mt-1 text-xs text-ink-dark">
              {new Date(msgQ.data.message.createdAt).toLocaleString()}
            </p>

            <div className="mt-6">
              <InboxMarkdown source={msgQ.data.message.body} />
            </div>

            <div className="mt-6">
              <CtaForMessage msg={msgQ.data.message} />
            </div>

            <div className="mt-8 flex justify-end">
              <button
                type="button"
                onClick={() => markUnread.mutate({ id })}
                disabled={
                  markUnread.isPending || msgQ.data.message.readAt == null
                }
                className="text-xs font-semibold text-navy/70 hover:text-navy disabled:opacity-40"
                data-testid="button-mark-unread"
              >
                Mark as unread
              </button>
            </div>
          </article>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

function CtaForMessage({ msg }: { msg: Msg }): JSX.Element | null {
  switch (msg.messageType) {
    case "VOUCHER_EXPIRING_SOON": {
      const p = msg.payload;
      return (
        <Link
          href={`/vouchers/${p.voucherId}`}
          className="btn-primary block w-full text-center"
          data-testid="cta-view-voucher"
        >
          View voucher
        </Link>
      );
    }
    case "MARKETPLACE_CLAIM_CREDITED": {
      const p = msg.payload;
      return (
        <Link
          href="/cards"
          className="btn-primary block w-full text-center"
          data-testid="cta-view-card"
        >
          View {p.tenantName} card
        </Link>
      );
    }
    case "TENANT_MESSAGE":
    case "M42_ANNOUNCEMENT": {
      const p = msg.payload;
      if (p && p.ctaUrl && p.ctaLabel && /^https?:/i.test(p.ctaUrl)) {
        return (
          <a
            href={p.ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary block w-full text-center"
            data-testid="cta-tenant-link"
          >
            {p.ctaLabel}
          </a>
        );
      }
      return null;
    }
    default:
      return null;
  }
}

function labelForType(t: Msg["messageType"]): string {
  switch (t) {
    case "VOUCHER_EXPIRING_SOON":
      return "Voucher reminder";
    case "MARKETPLACE_CLAIM_CREDITED":
      return "Claim credited";
    case "TENANT_MESSAGE":
      return "Merchant message";
    case "M42_ANNOUNCEMENT":
      return "Announcement";
    default:
      return "Message";
  }
}

function formatAuthor(msg: Msg): string {
  if (msg.authorType === "TENANT" && msg.authorTenantName) {
    return msg.authorTenantName;
  }
  if (msg.authorType === "M42_PLATFORM") return "Orbit";
  return "System";
}
